import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import _ from 'lodash'
import { CircularProgress, LinearProgress } from '@mui/material'
import Box from '@mui/material/Box'
import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepButton from '@mui/material/StepButton'
import Button from '@mui/material/Button'
import { checkObjectPropertiesFilled } from '../../../../../../utils/functions'
import {
    CoursePriceDetailsState,
    CoursePriceState,
    CourseSubjectPriceState,
} from '../../../../../../features/types'
import { coursePriceApi } from '../../../../../../features/course/coursePrice'
import { courseApiSlice } from '../../../../../../features/course/courseSlice'
import { courseSubjectApiSlice } from '../../../../../../features/course/courseSubjectSlice'
import { courseSubjectPriceApi } from '../../../../../../features/course/courseSubjectPrice'
import PriceTab from '../tabs/PriceTab'
import SubjectsTab from '../tabs/SubjectsTab'
import PromoTab from '../tabs/PromoTab'
import BundleTab from '../tabs/BundleTab'
import formInitialValues from './FormModel/formInitialValues'
import validationSchema from './FormModel/validationSchema'

const steps = ['coursePrice', 'subjects', 'promoCompaign', 'packageDiscount']

interface TabPanelProps {
    children?: React.ReactNode
    index: number
    value: number
    style?: any
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    )
}

interface Props {
    editCoursePrice?: CoursePriceDetailsState
}

const styles = {
    fullWidth: {
        width: '100%',
    },
    panelContainer: {
        minHeight: '500px',
    },
    flexRow: {
        display: 'flex',
        flexDirection: 'row',
    },
}

const Steppers: React.FC<Props> = ({ editCoursePrice }) => {
    const editMode = !!editCoursePrice
    const [activeStep, setActiveStep] = useState<number>(0)
    const [stepValues, setStepValues] = useState([])
    const [coursePriceId, setCoursePriceId] = useState<number>(
        editCoursePrice?.id ?? null,
    )
    const [courseSubjectsValids, setCourseSubjectsValids] = useState<boolean[]>(
        [],
    )
    const [courseId, setCourseId] = useState<number>(null)
    const [completed, setCompleted] = useState<{
        [k: number]: boolean
    }>({})

    const { formatMessage } = useIntl()
    const { data: courses = [], isFetching: isFetchingCourses } =
        courseApiSlice.useFetchAllCoursesQuery()
    const { data: courseSubjects = [], isFetching: isFetchingSubjects } =
        courseSubjectApiSlice.useFetchActiveCourseSubjectsQuery(
            courseId || editCoursePrice?.courseId,
            {
                skip: !courseId && !editCoursePrice?.courseId,
            },
        )
    const { data: courseSubjectsPrice = [] } =
        courseSubjectPriceApi.useGetCourseSubjectsPricesByCoursePriceQuery(
            editCoursePrice?.id,
            { skip: !editCoursePrice },
        )

    const [createCoursePrice, { isLoading: isCreatingCoursePrice }] =
        coursePriceApi.useCreateCoursePriceMutation()
    const [updateCoursePrice, { isLoading: isUpdatingCoursePrice }] =
        coursePriceApi.useUpdateCoursePriceMutation()
    const [
        createCourseSubjectsPrice,
        { isLoading: isCreatingCourseSubjectsPrice },
    ] = courseSubjectPriceApi.useCreateCourseSubjectsPriceMutation()

    const isLoading =
        isCreatingCoursePrice ||
        isUpdatingCoursePrice ||
        isCreatingCourseSubjectsPrice

    const formik = useFormik({
        enableReinitialize: true,
        initialValues:
            activeStep <= 1 &&
            (stepValues[activeStep] ??
                formInitialValues[activeStep](
                    editCoursePrice,
                    coursePriceId,
                    courseSubjectsPrice,
                    courseSubjects,
                )),
        validationSchema:
            activeStep === 0 && validationSchema[activeStep]({ formatMessage }),
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: async () => {
            if (activeStep === 0) {
                if (editMode || formik.values.id) {
                    handleUpdateCoursePrice()
                } else {
                    handleCreateCoursePrice()
                }
            } else if (activeStep === 1) {
                const mappedCourseSubjects =
                    formik.values.courseSubjectsPrice.map((item) => {
                        return {
                            price: item.price,
                        }
                    })
                const courseSubjectsValidations =
                    checkObjectPropertiesFilled(mappedCourseSubjects)

                if (courseSubjectsValidations.includes(false)) {
                    setCourseSubjectsValids(courseSubjectsValidations)
                } else {
                    handleCreateCourseSubjectsPrice()
                }
            }
        },
    })

    const handleCreateCoursePrice = async () => {
        try {
            const result = await createCoursePrice(
                formik.values as CoursePriceState,
            ).unwrap()
            setCoursePriceId(result)
            setCourseId(formik.values.courseId)
            updateStepValues(0, { ...formik.values, id: result })
            toast.success(formatMessage({ id: 'savedSuccess' }))
            onNextTab()
        } catch (error) {}
    }

    const handleUpdateCoursePrice = async () => {
        try {
            await updateCoursePrice(formik.values as CoursePriceState).unwrap()
            updateStepValues(0, { ...formik.values })
            toast.success(formatMessage({ id: 'updatedSuccess' }))
            onNextTab()
        } catch (error) {}
    }

    const handleCreateCourseSubjectsPrice = async () => {
        try {
            await createCourseSubjectsPrice(
                formik.values.courseSubjectsPrice,
            ).unwrap()
            updateStepValues(1, { ...formik.values })
            toast.success(formatMessage({ id: 'savedSuccess' }))
            onNextTab()
        } catch (error) {}
    }

    const updateStepValues = (
        index: number,
        value: CoursePriceState | CourseSubjectPriceState,
    ) => {
        const newSteps = _.cloneDeep(stepValues)
        newSteps[index] = value
        setStepValues(newSteps)
    }

    const totalSteps = () => {
        return steps.length
    }

    const completedSteps = () => {
        return Object.keys(completed).length
    }

    const isLastStep = () => {
        return activeStep === totalSteps() - 1
    }

    const allStepsCompleted = () => {
        return completedSteps() === totalSteps()
    }

    const handleNext = () => {
        const newActiveStep =
            isLastStep() && !allStepsCompleted()
                ? // It's the last step, but not all steps have been completed,
                  // find the first step that has been completed
                  steps.findIndex((step, i) => !(i in completed))
                : activeStep + 1

        formik.setValues(stepValues[newActiveStep])
        setActiveStep(newActiveStep)
    }

    const handleBack = () => {
        const newValues = stepValues[activeStep - 1]
        formik.setValues(newValues)
        setActiveStep((prevActiveStep) => prevActiveStep - 1)
    }

    const handleStep = (step: number) => () => {
        formik.setValues(stepValues[step])
        setActiveStep(step)
    }

    const onNextTab = () => {
        const newCompleted = completed
        newCompleted[activeStep] = true
        setCompleted(newCompleted)
        handleNext()
    }

    // useEffect(() => {
    //     setCourseSubjectsValids([])
    // }, [formik.values])

    return (
        <Box sx={styles.fullWidth}>
            <Stepper
                nonLinear
                activeStep={activeStep}
            >
                {steps.map((label, index) => (
                    <Step
                        key={label}
                        completed={completed[index]}
                    >
                        <StepButton
                            color="inherit"
                            onClick={handleStep(index)}
                        >
                            {formatMessage({ id: label })}
                        </StepButton>
                    </Step>
                ))}
            </Stepper>

            {isFetchingCourses && <LinearProgress />}

            <form onSubmit={formik.handleSubmit}>
                <Box sx={styles.panelContainer}>
                    <TabPanel
                        value={activeStep}
                        index={0}
                    >
                        <PriceTab
                            courses={courses}
                            values={formik.values as CoursePriceState}
                            handleChange={formik.handleChange}
                            handleBlur={formik.handleBlur}
                            errors={
                                formik.errors as unknown as CoursePriceState
                            }
                        />
                    </TabPanel>

                    <TabPanel
                        value={activeStep}
                        index={1}
                    >
                        <SubjectsTab
                            values={formik.values}
                            courseSubjects={courseSubjects}
                            handleChange={formik.handleChange}
                            handleBlur={formik.handleBlur}
                            isFetchingSubjects={isFetchingSubjects}
                            courseSubjectsValids={courseSubjectsValids}
                            errors={
                                formik.errors as unknown as CourseSubjectPriceState
                            }
                        />
                    </TabPanel>

                    <TabPanel
                        value={activeStep}
                        index={2}
                    >
                        <PromoTab coursePriceId={coursePriceId} />
                    </TabPanel>

                    <TabPanel
                        value={activeStep}
                        index={3}
                    >
                        <BundleTab coursePriceId={coursePriceId} />
                    </TabPanel>
                </Box>

                <Box
                    sx={styles.flexRow}
                    pt={2}
                >
                    <Button
                        color="inherit"
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        sx={{ mr: 1 }}
                    >
                        {formatMessage({ id: 'back' })}
                    </Button>

                    <Box sx={{ flex: '1 1 auto' }} />

                    {activeStep <= 1 ? (
                        <Button
                            variant={'contained'}
                            onClick={() => formik.handleSubmit()}
                            disabled={
                                _.isEqual(
                                    formik.values,
                                    stepValues[activeStep],
                                ) || isLoading
                            }
                        >
                            {isLoading ? (
                                <CircularProgress
                                    size={20}
                                    style={{ marginRight: '8px' }}
                                />
                            ) : (
                                formatMessage({ id: 'save' })
                            )}
                        </Button>
                    ) : (
                        <Button
                            onClick={handleNext}
                            sx={{ mr: 1 }}
                        >
                            {formatMessage({ id: 'next' })}
                        </Button>
                    )}
                </Box>
            </form>
        </Box>
    )
}

export default Steppers
